import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reply } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";

export default function Inquiries() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [decision, setDecision] = useState("continue");
  const [replyText, setReplyText] = useState("");
  const isStaff = ["admin", "staff"].includes(user.role);

  const load = async () => {
    const r = await api.get("/inquiries");
    setItems(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const sendReply = async () => {
    try {
      await api.put(`/inquiries/${replyTo.inquiry_id}/reply`, { decision, reply: replyText });
      toast.success(t("saved"));
      setReplyTo(null);
      setReplyText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("inquiries")}</h1>
      {!isStaff && <p className="text-sm text-slate-500">{t("askWeather")} → {t("schedule")}</p>}

      <div className="gfa-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("date")}</TableHead>
              <TableHead>{t("schedule")}</TableHead>
              {isStaff && <TableHead>{t("parents")}</TableHead>}
              <TableHead>{t("message")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("decision")}</TableHead>
              {isStaff && <TableHead>{t("actions")}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((q) => (
              <TableRow key={q.inquiry_id} data-testid={`inquiry-row-${q.inquiry_id}`}>
                <TableCell className="whitespace-nowrap">{q.schedule_date}</TableCell>
                <TableCell className="font-medium">{q.schedule_title}</TableCell>
                {isStaff && <TableCell>{q.parent_name}</TableCell>}
                <TableCell className="max-w-[200px] truncate text-sm">{q.message || "-"}</TableCell>
                <TableCell>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${q.status === "answered" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {q.status === "answered" ? t("answered") : t("pending")}
                  </span>
                </TableCell>
                <TableCell className="text-sm">
                  {q.decision ? (q.decision === "continue" ? t("continueTraining") : t("cancelTraining")) : "-"}
                  {q.reply && <p className="text-xs text-slate-400">{q.reply}</p>}
                </TableCell>
                {isStaff && (
                  <TableCell>
                    {q.status === "pending" && (
                      <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`reply-inquiry-${q.inquiry_id}`}
                        onClick={() => { setReplyTo(q); setDecision("continue"); setReplyText(""); }}>
                        <Reply size={13} className="mr-1" /> {t("reply")}
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-400">{t("noInquiries")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!replyTo} onOpenChange={(v) => !v && setReplyTo(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("reply")} — {replyTo?.schedule_title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <RadioGroup value={decision} onValueChange={setDecision} data-testid="decision-radio">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="continue" id="d-continue" data-testid="decision-continue" />
                <Label htmlFor="d-continue">{t("continueTraining")}</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="cancel" id="d-cancel" data-testid="decision-cancel" />
                <Label htmlFor="d-cancel">{t("cancelTraining")}</Label>
              </div>
            </RadioGroup>
            <Textarea data-testid="reply-text" placeholder={t("message")} value={replyText} onChange={(e) => setReplyText(e.target.value)} />
            <Button onClick={sendReply} data-testid="send-reply-btn" className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">{t("submit")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
